'use client';

import { createContext, useContext } from 'react';
import type { VisibleGoLiveScope } from '../lib/billing';
import { isModulePathAccessible, isPayrollEntitlementPath } from '../lib/module-access';

interface ModuleContextValue {
  /** Authoritative tenant billing plan; null means the plan could not be verified. */
  billingPlan?: string | null;
  /** Array of enabled module paths, or null only for legacy all-access tenants */
  enabledModules: string[] | null;
  /** Current tenant id, used for tenant-scoped UAT surface gates. */
  tenantId?: string | null;
  /** True only when the current tenant is explicitly marked demo in the DB. */
  isDemoTenant?: boolean;
  /** Demo tenants only: plan-derived modules that gate payroll paths. */
  demoPlanModules?: string[] | null;
  /** Whether this is a CI-free trial tenant */
  isCiFree: boolean;
  /** Whether the module configuration has been loaded from the server */
  modulesLoaded?: boolean;
  /** Whether the subscription is canceled/suspended (empty modules due to billing, not plan) */
  isSubscriptionBlocked?: boolean;
  /**
   * True when the module configuration could not be READ, as opposed to being
   * read and found not to include this page.
   *
   * The shell falls back to `enabledModules = []` on a failed load, which is
   * indistinguishable from "no modules licensed" — so without this flag
   * ModuleGate reports a data-visibility failure as a billing problem and sends
   * the provider to the wrong person. Observed live 2026-07-25.
   */
  moduleLoadError?: boolean;
  /** Active launch profile, used to keep primary navigation compact. */
  visibleGoLiveScope?: VisibleGoLiveScope;
  /** Tenant-level navigation curation applied after hard gates. */
  tenantHiddenNavigationPaths?: string[];
  /** Tenant launch profile that curates frontline discovery to self-service only. */
  frontlineSelfServiceOnly?: boolean;
  /** Server-resolved tenant/global shift-swaps feature switch. */
  shiftSwapsEnabled?: boolean;
  payrollRebuildOn?: boolean;
}

const ModuleContext = createContext<ModuleContextValue>({
  enabledModules: [],
  tenantId: null,
  isDemoTenant: false,
  demoPlanModules: null,
  isCiFree: false,
  modulesLoaded: false,
  isSubscriptionBlocked: false,
  moduleLoadError: false,
  visibleGoLiveScope: null,
  tenantHiddenNavigationPaths: [],
  frontlineSelfServiceOnly: false,
  shiftSwapsEnabled: false,
  payrollRebuildOn: false,
});

export const ModuleProvider = ModuleContext.Provider;

/** Access the current module context (enabledModules + plan flags) */
export function useModules(): ModuleContextValue {
  return useContext(ModuleContext);
}

/** Check if a specific path is available for the current tenant's enabled modules.
 *
 * The shared matcher allows:
 *  - exact module paths
 *  - detail routes nested under an enabled module
 *  - a small allowlist of landing pages like '/compliance' and '/residential'
 *
 * It intentionally does NOT let a broad parent like '/analytics' unlock every
 * known child route such as '/analytics/board-pack'.
 */
export function useIsModuleEnabled(path: string): boolean {
  const { enabledModules, isDemoTenant, demoPlanModules } = useModules();
  if (isDemoTenant && !isPayrollEntitlementPath(path)) return true;
  return isModulePathAccessible(path, enabledModules, { isDemoTenant, demoPlanModules });
}
